import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import CustomizeIcon from '@atlaskit/icon/core/customize';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import {
	PRODUCTS_WITH_INLINE_FILTERS,
	SEARCH_DIALOG_WIDTH_OBSERVER_ID,
} from '../../../../common/constants/quick-find';
import { type QueryParams } from '../../../../common/types';
import { type NavigateCallbackEvent, useAppContext } from '../../../../common/ui/app-context';
import { useElementWidth } from '../../../../common/ui/width-observer';
import { serveQFAppSelectorExperiment } from '../../../../common/utils/experiments';
import { onMoreProductFiltersSelected } from '../../../../common/utils/quick-find/events/product-filter-more-selected';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';
import { useFiltersDiscoverability2Enabled, usePrimaryProduct } from '../../../../controllers/store/bootstrap';
import { useQuickFindActions, useQuickFindQuery } from '../../../../controllers/store/quick-find';
import { useSearchActions, useSelectedProduct } from '../../../../controllers/store/search';

import { InlineAppSelector } from './inline-app-selector';
import { InlineProductFilters } from './inline-product-filters';
import { i18n } from './messages';

const containerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	paddingInline: 'space.200',
	paddingBlockStart: 'space.025',
	paddingBlockEnd: 'space.075',
});

const containerSpaceBetweenStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
});

const filterDropdownIconStyles = xcss({
	padding: 'space.050',
	marginInlineEnd: 'space.100',
});

export type OnAllFiltersClickCallbackProps = {
	filterDropdownOpen?: boolean;
	action?: 'filterByPrimaryProduct' | 'searchByAllApps' | 'none';
	event?: NavigateCallbackEvent;
};

export const QuickFindFilters = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const dialogWidth = useElementWidth(SEARCH_DIALOG_WIDTH_OBSERVER_ID);

	const { generateSearchUrl, onNavigate, queryParams } = useAppContext();
	const { setQuickFindOpen } = useQuickFindActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const quickFindQuery = useQuickFindQuery();
	const { clearAllFilters } = useSearchActions();
	const [selectedProduct] = useSelectedProduct();
	const [primaryProduct] = usePrimaryProduct();
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const serveQFAppSelector = serveQFAppSelectorExperiment(primaryProduct, {
		fireExperimentExposure: false,
	});

	const isDialogAbove275px = dialogWidth > 275;
	const displayCustomizeIcon = isDialogAbove275px || !selectedProduct;

	const onAllFiltersClickCallback = useCallback(
		({ action = 'none', filterDropdownOpen = false, event }: OnAllFiltersClickCallbackProps) => {
			setQuickFindOpen(false);

			fireAnalyticsEvent(
				onMoreProductFiltersSelected({
					actionSubjectId: 'more-product-filters',
					attributes: commonAttributes,
					nonPrivacySafeAttributes,
				}),
			);

			// TODO: This is a temporary measure until we do QS-6716 post Team 25 where it syncs filters with FPS.
			// For now, executing a search query on FPS search will always clear the filters in QF as FPS filters
			// is the source of truth for filter state.
			clearAllFilters();

			let newQueryParams: QueryParams = {};
			if (action === 'searchByAllApps') {
				newQueryParams = {};
			} else if (action === 'none') {
				newQueryParams = queryParams;
			} else if (action === 'filterByPrimaryProduct') {
				newQueryParams = { ...queryParams, product: primaryProduct };
			}

			const onNavigateCallback =
				typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

			onNavigateCallback(
				generateSearchUrl({
					...newQueryParams,
					...(filtersDiscoverability2Enabled ? { filterDropdownOpen } : {}),
					text: quickFindQuery,
				}),
				'push',
				event,
			);
		},
		[
			clearAllFilters,
			commonAttributes,
			fireAnalyticsEvent,
			generateSearchUrl,
			nonPrivacySafeAttributes,
			onNavigate,
			primaryProduct,
			queryParams,
			quickFindQuery,
			setQuickFindOpen,
			filtersDiscoverability2Enabled,
		],
	);

	if (!PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct)) {
		return (
			<Box xcss={[containerStyles, containerSpaceBetweenStyles]}>
				{displayCustomizeIcon && (
					<Box xcss={filterDropdownIconStyles}>
						<CustomizeIcon label={formatMessage(i18n.filterByIcon)} />
					</Box>
				)}
				<InlineAppSelector onAllFiltersClickCallback={onAllFiltersClickCallback} />
				<InlineProductFilters onAllFiltersClickCallback={onAllFiltersClickCallback} />
			</Box>
		);
	}

	return (
		<Box xcss={[containerStyles, containerSpaceBetweenStyles]}>
			<InlineProductFilters onAllFiltersClickCallback={onAllFiltersClickCallback} />
			{!serveQFAppSelector && (
				<InlineAppSelector onAllFiltersClickCallback={onAllFiltersClickCallback} />
			)}
		</Box>
	);
};
